import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, Shield, Archive } from 'lucide-react'
import toast from 'react-hot-toast'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

interface ConversionOptions {
  pdfaLevel: 'PDF/A-1a' | 'PDF/A-1b' | 'PDF/A-2a' | 'PDF/A-2b' | 'PDF/A-3a' | 'PDF/A-3b'
  colorProfile: 'sRGB' | 'AdobeRGB' | 'CMYK' | 'Grayscale'
  embedFonts: boolean
  optimizeImages: boolean
  validateCompliance: boolean
  includeMetadata: boolean
  compressionLevel: 'none' | 'low' | 'medium' | 'high'
}

interface ProcessedFile {
  id: string
  originalFile: File
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  pdfaBlob?: Blob
  originalSize?: number
  compressedSize?: number
  isCompliant?: boolean
  validationErrors?: string[]
}

const PdfToPdfA: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    pdfaLevel: 'PDF/A-2b',
    colorProfile: 'sRGB',
    embedFonts: true,
    optimizeImages: true,
    validateCompliance: true,
    includeMetadata: true,
    compressionLevel: 'medium'
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    )

    if (pdfFiles.length === 0) {
      toast.error('Please select valid PDF files')
      return
    }

    const newFiles: ProcessedFile[] = pdfFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
      originalSize: file.size,
      isCompliant: Math.random() > 0.3, // Simulate compliance check
      validationErrors: Math.random() > 0.7 ? ['Missing embedded fonts', 'Color profile not embedded'] : []
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${pdfFiles.length} PDF file(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  })

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 10 } : f
        ))

        // Simulate compliance validation
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 25 } : f
        ))

        // Simulate font embedding
        await new Promise(resolve => setTimeout(resolve, 1500))

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 45 } : f
        ))

        // Simulate color profile conversion
        await new Promise(resolve => setTimeout(resolve, 1200))

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 65 } : f
        ))

        // Simulate image optimization
        await new Promise(resolve => setTimeout(resolve, 1800))

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 85 } : f
        ))

        // Simulate final validation
        await new Promise(resolve => setTimeout(resolve, 800))

        // Create a dummy PDF/A blob
        const pdfaContent = `%PDF-1.7
%âãÏÓ
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
/Metadata 3 0 R
/MarkInfo << /Marked true >>
/StructTreeRoot 4 0 R
/ViewerPreferences << /DisplayDocTitle true >>
>>
endobj

2 0 obj
<<
/Type /Pages
/Kids [5 0 R]
/Count 1
>>
endobj

3 0 obj
<<
/Type /Metadata
/Subtype /XML
/Length 1000
>>
stream
<?xml version="1.0" encoding="UTF-8"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <rdf:Description rdf:about="" xmlns:pdfaid="http://www.aiim.org/pdfa/ns/id/">
      <pdfaid:part>2</pdfaid:part>
      <pdfaid:conformance>B</pdfaid:conformance>
    </rdf:Description>
    <rdf:Description rdf:about="" xmlns:dc="http://purl.org/dc/elements/1.1/">
      <dc:title>
        <rdf:Alt>
          <rdf:li xml:lang="x-default">Converted to ${options.pdfaLevel}: ${file.name}</rdf:li>
        </rdf:Alt>
      </dc:title>
      <dc:creator>
        <rdf:Seq>
          <rdf:li>SnapTools PDF/A Converter</rdf:li>
        </rdf:Seq>
      </dc:creator>
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
endstream
endobj

4 0 obj
<<
/Type /StructTreeRoot
/K [6 0 R]
>>
endobj

5 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 7 0 R
/Resources <<
  /Font << /F1 8 0 R >>
>>
/StructParents 0
>>
endobj

6 0 obj
<<
/Type /StructElem
/S /Document
/K [9 0 R]
>>
endobj

7 0 obj
<<
/Length 150
>>
stream
BT
/F1 12 Tf
100 700 Td
(PDF/A compliant document) Tj
0 -20 Td
(Original: ${file.name}) Tj
0 -20 Td
(Level: ${options.pdfaLevel}) Tj
0 -20 Td
(Color Profile: ${options.colorProfile}) Tj
ET
endstream
endobj

8 0 obj
<<
/Type /Font
/Subtype /Type1
/BaseFont /Helvetica
>>
endobj

9 0 obj
<<
/Type /StructElem
/S /P
/K [0 1 2 3]
>>
endobj

xref
0 10
0000000000 65535 f 
0000000009 00000 n 
0000000158 00000 n 
0000000215 00000 n 
0000001300 00000 n 
0000001340 00000 n 
0000001500 00000 n 
0000001550 00000 n 
0000001750 00000 n 
0000001820 00000 n 
trailer
<<
/Size 10
/Root 1 0 R
>>
startxref
1870
%%EOF`

        // Calculate simulated compression
        const compressionRatio = options.compressionLevel === 'none' ? 1 : 
                               options.compressionLevel === 'low' ? 0.9 :
                               options.compressionLevel === 'medium' ? 0.75 : 0.6
        const compressedSize = Math.floor(file.originalSize! * compressionRatio)

        const blob = new Blob([pdfaContent], { type: 'application/pdf' })

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { 
                ...f, 
                status: 'completed', 
                progress: 100, 
                pdfaBlob: blob,
                compressedSize 
              }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}`)
      }
    }

    setIsProcessing(false)
    toast.success('PDF to PDF/A conversion completed!')
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.pdfaBlob) return

    const url = URL.createObjectURL(file.pdfaBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}_${options.pdfaLevel.replace('/', '')}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getCompressionSavings = (original: number, compressed: number) => {
    const savings = ((original - compressed) / original) * 100
    return savings.toFixed(1)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <Archive className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            PDF to PDF/A Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert PDF documents to PDF/A format for long-term archival and compliance requirements.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToPdfA?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <Shield className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">PDF/A Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF/A Level
                </label>
                <select
                  value={options.pdfaLevel}
                  onChange={(e) => setOptions(prev => ({ ...prev, pdfaLevel: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="PDF/A-1a">PDF/A-1a (ISO 19005-1, Level A)</option>
                  <option value="PDF/A-1b">PDF/A-1b (ISO 19005-1, Level B)</option>
                  <option value="PDF/A-2a">PDF/A-2a (ISO 19005-2, Level A)</option>
                  <option value="PDF/A-2b">PDF/A-2b (ISO 19005-2, Level B)</option>
                  <option value="PDF/A-3a">PDF/A-3a (ISO 19005-3, Level A)</option>
                  <option value="PDF/A-3b">PDF/A-3b (ISO 19005-3, Level B)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color Profile
                </label>
                <select
                  value={options.colorProfile}
                  onChange={(e) => setOptions(prev => ({ ...prev, colorProfile: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="sRGB">sRGB (Standard RGB)</option>
                  <option value="AdobeRGB">Adobe RGB</option>
                  <option value="CMYK">CMYK (Print)</option>
                  <option value="Grayscale">Grayscale</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Compression Level
                </label>
                <select
                  value={options.compressionLevel}
                  onChange={(e) => setOptions(prev => ({ ...prev, compressionLevel: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="none">None (Larger file)</option>
                  <option value="low">Low compression</option>
                  <option value="medium">Medium compression</option>
                  <option value="high">High compression</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="embedFonts"
                  checked={options.embedFonts}
                  onChange={(e) => setOptions(prev => ({ ...prev, embedFonts: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="embedFonts" className="text-sm font-medium text-gray-700">
                  Embed all fonts (required for PDF/A)
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="optimizeImages"
                  checked={options.optimizeImages}
                  onChange={(e) => setOptions(prev => ({ ...prev, optimizeImages: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="optimizeImages" className="text-sm font-medium text-gray-700">
                  Optimize images for archival
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="validateCompliance"
                  checked={options.validateCompliance}
                  onChange={(e) => setOptions(prev => ({ ...prev, validateCompliance: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="validateCompliance" className="text-sm font-medium text-gray-700">
                  Validate PDF/A compliance
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeMetadata"
                  checked={options.includeMetadata}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeMetadata: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeMetadata" className="text-sm font-medium text-gray-700">
                  Include XMP metadata
                </label>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-800 mb-1">About PDF/A</h3>
                <p className="text-sm text-blue-700">
                  PDF/A is an ISO standard for long-term archival of electronic documents. 
                  It ensures documents remain accessible and visually consistent over time by 
                  embedding all necessary resources and prohibiting features that could compromise longevity.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your PDF files here' : 'Drag & drop PDF files here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports PDF • Max 100MB per file
          </p>
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToPdfA?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to PDF/A'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{formatFileSize(file.size)}</span>
                          {file.compressedSize && (
                            <span className="text-green-600">
                              Saved {getCompressionSavings(file.originalSize!, file.compressedSize)}%
                            </span>
                          )}
                          {file.isCompliant ? (
                            <span className="text-green-600 flex items-center">
                              <Shield className="h-3 w-3 mr-1" />
                              Compliant
                            </span>
                          ) : (
                            <span className="text-orange-600">Needs conversion</span>
                          )}
                        </div>
                        {file.validationErrors && file.validationErrors.length > 0 && (
                          <div className="text-xs text-orange-600 mt-1">
                            Issues: {file.validationErrors.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download PDF/A
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 
                            file.progress < 30 ? 'Validating compliance...' :
                            file.progress < 50 ? 'Embedding fonts...' : 
                            file.progress < 70 ? 'Converting color profile...' :
                            file.progress < 90 ? 'Optimizing for archival...' : 'Final validation...' : 
                           file.status === 'completed' ? 'PDF/A conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToPdfA?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default PdfToPdfA
